import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

export const EntradaStatCard = ({ icon, title, value, suffix }) => {
  const reduceMotion = useReducedMotion();
  const valueRef = useRef(null);
  const [isValueOverflowing, setIsValueOverflowing] = useState(false);

  useEffect(() => {
    const valueNode = valueRef.current;
    if (!valueNode) return undefined;

    const checkOverflow = () => {
      setIsValueOverflowing(valueNode.scrollWidth > valueNode.clientWidth);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(valueNode);

    return () => resizeObserver.disconnect();
  }, [value]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        minHeight: 82,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 1.5,
            bgcolor: 'primary.container',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: '1 1 auto', maxWidth: '100%' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
          <Tooltip
            title={isValueOverflowing ? `${value}${suffix ? ` ${suffix}` : ''}` : ''}
            arrow
            placement="top"
            disableHoverListener={!isValueOverflowing}
            disableFocusListener={!isValueOverflowing}
            disableTouchListener={!isValueOverflowing}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.5,
                minHeight: 28,
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <Typography
                  key={value}
                  ref={valueRef}
                  component={motion.span}
                  initial={reduceMotion ? false : { opacity: 0, y: 8, filter: 'blur(2px)' }}
                  animate={reduceMotion ? undefined : { opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: -8, filter: 'blur(2px)' }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  sx={{
                    display: 'block',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: 24, xl: 26 },
                    fontWeight: 900,
                    lineHeight: '31px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {value}
                </Typography>
              </AnimatePresence>
              {suffix && (
                <Typography
                  component="span"
                  sx={{ color: 'text.secondary', fontSize: 14, flex: '0 0 auto' }}
                >
                  {suffix}
                </Typography>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );
};
